/* eslint-disable jam3/no-sanitizer-with-danger */

import { Icon } from '@gameday/react-components/components'
import theme, { rem } from '@gameday/react-components/utils/theme'
import styled from 'styled-components'

import gameDayLogoSvg from '../../../../assets/images/game_day.svg'

const StyledFooter = styled.footer`
  border-top: 1px solid ${theme.borders.borderColorDark};
  background-color: ${theme.colors.white};
  position: relative;
  z-index: 10;
`

const StyledFooterMain = styled.div`
  display: flex;
  justify-content: center;
`

const StyledFooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${rem(64)};
  padding: 0 ${rem(18)};
  border-top: 1px solid ${theme.borders.borderColorDark};

  ${theme.breakpoints.down('xs')} {
    margin-bottom: ${rem(54)};
  }
`

const StyledFooterInner = styled.div`
  width: 100%;
  max-width: ${rem(900)};
  display: flex;
  justify-content: center;
  padding: ${rem(50)} ${rem(15)};

  ${theme.breakpoints.down('xs')} {
    flex-wrap: wrap;
    padding: ${rem(30)} ${rem(10)};
  }
`

const StyledLogo = styled.div`
  width: ${rem(130)};
  margin-right: ${rem(20)};

  > img {
    display: block;
    width: 100%;
  }

  ${theme.breakpoints.down('xs')} {
    display: none;
  }
`

const StyledColumn = styled.div`
  max-width: ${rem(225)};
  margin: 0 ${rem(20)};

  ${theme.breakpoints.down('xs')} {
    margin: 0 ${rem(10)} ${rem(20)};
    max-width: calc(50% - ${rem(20)});
    width: calc(50% - ${rem(20)});
  }
`

const StyledColumnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${rem(30)};

  @media (max-width: 540px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto auto;

    > div {
      max-width: none !important;
      width: auto;
    }
  }
`

const StyledColumnLabel = styled.div`
  font-size: ${theme.typography.fontSizeBase};
  font-weight: ${theme.typography.fontWeightBold};
  color: ${theme.body.textColorDark};
  margin-bottom: ${rem(14)};
`

const StyledColumnLinks = styled.div`
  & > a {
    display: block;
    color: ${theme.anchor.color};
    text-decoration: ${theme.anchor.textDecoration};
    font-size: ${theme.typography.fontSizeTinyish};
    font-weight: ${theme.typography.fontWeightBolder};
    line-height: 1.333;
    position: relative;
    padding-left: ${rem(30)};
    margin-bottom: ${rem(8)};

    & > .custom-icon {
      position: absolute;
      top: -${rem(4)};
      left: 0;
      width: ${rem(24)};
      height: ${rem(24)};
      color: ${theme.body.textColorLight};
    }

    &:hover {
      color: ${theme.anchor.hoverColor};
    }
  }
`

const StyledPartners = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${rem(150)};

  ${theme.breakpoints.down('xs')} {
    display: flex;
    flex-direction: column;
  }
`

const StyledPartner = styled.div`
  a {
    display: block;
  }

  img {
    max-width: ${rem(95)};
    height: auto;
  }

  & + & {
    margin: ${rem(10)} 0 0;
  }

  ${theme.breakpoints.up('sm')} {
    margin-right: ${rem(20)};
  }
`

const StyledGamedayLogo = styled.img`
  width: auto;
  height: ${rem(35)};
`

const StyledFooterText = styled.div`
  @media (max-width: 540px) {
    display: flex;
    flex-direction: column;
  }

  font-size: ${theme.typography.fontSizeTinyish};
  font-weight: ${theme.typography.fontWeightNormal};
  color: ${theme.body.textColor};
  text-align: right;

  & > strong {
    font-weight: ${theme.typography.fontWeightBolder};
  }

  & > a {
    display: inline-block;
    margin-left: ${rem(20)};
    color: ${theme.anchor.color};
    font-weight: ${theme.typography.fontWeightBolder};
    text-decoration: ${theme.anchor.textDecoration};

    &:hover {
      color: ${theme.anchor.hoverColor};
    }
  }
`

const StyledSocialIcons = styled.div`
  display: flex;
  margin-top: ${rem(10)};

  & > a {
    display: block;
    width: ${rem(24)};
    height: ${rem(24)};
    color: ${theme.body.textColor};
    margin-right: ${rem(4)};
    overflow: hidden;

    & > .label {
      position: absolute;
      top: -1000px;
      left: -1000px;
    }
  }
`

const normalizeURL = url => (url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`)

const RegistrationFooter = ({ isEvent, logo, event, organisation, paymentGateway = [], subdomain }) => {
  const { name: eventName, venueName, address } = event || {}

  const {
    name: orgName,
    email,
    phone,
    website,
    socialFacebook,
    socialInstagram,
    socialTwitter,
    socialYoutube,
    socialTiktok,
    customLink1Url,
    customLink1Label,
    customLink1Image,
    customLink2Url,
    customLink2Label,
    customLink2Image,
    customLink3Url,
    customLink3Label,
    customLink3Image,
  } = organisation || {}

  const customLinks = [
    { url: customLink1Url, label: customLink1Label, image: customLink1Image },
    { url: customLink2Url, label: customLink2Label, image: customLink2Image },
    { url: customLink3Url, label: customLink3Label, image: customLink3Image },
  ].filter(link => link.url && link.label)

  const { street, city, postalCode, state } = address || {}

  const addressParts = [street, city, postalCode, state].filter(a => !!a)
  const addressString = addressParts.join(', ')

  const sortedPaymentGateways = paymentGateway.sort((a, b) => {
    const { orderNumber: aOrderNumber = 0 } = a
    const { orderNumber: bOrderNumber = 0 } = b

    if (aOrderNumber < bOrderNumber) return -1
    if (aOrderNumber > bOrderNumber) return 1

    return 0
  })

  return (
    <StyledFooter>
      <StyledFooterMain>
        <StyledFooterInner>
          <StyledLogo>
            <img src={logo} alt="" />
          </StyledLogo>
          <StyledColumnWrapper>
            <StyledColumn>
              <StyledColumnLabel>About {orgName || 'Organisation'}</StyledColumnLabel>
              <StyledColumnLinks>
                {website && (
                  <a href={`//${website.replace(/^\s+http[s]*:\/\//, '')}`} target="_blank" rel="noreferrer">
                    <Icon iconSize={24} iconName="website" />
                    {website}
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`}>
                    <Icon iconSize={24} iconName="email-alt" />
                    {email}
                  </a>
                )}
                {phone && (
                  <a href={`tel:${phone}`} target="_blank" rel="noreferrer">
                    <Icon iconSize={24} iconName="phone" />
                    {phone}
                  </a>
                )}
              </StyledColumnLinks>
              <StyledSocialIcons>
                {socialFacebook && (
                  <a href={normalizeURL(socialFacebook)} target="_blank" rel="noopener noreferrer">
                    <Icon iconSize={24} iconName="facebook" />
                    <span className="label">Facebook</span>
                  </a>
                )}
                {socialInstagram && (
                  <a href={normalizeURL(socialInstagram)} target="_blank" rel="noopener noreferrer">
                    <Icon iconSize={24} iconName="instagram" />
                    <span className="label">Instagram</span>
                  </a>
                )}
                {socialTwitter && (
                  <a href={normalizeURL(socialTwitter)} target="_blank" rel="noopener noreferrer">
                    <Icon iconSize={22} iconName="twitter" />
                    <span className="label">Twitter</span>
                  </a>
                )}
                {socialYoutube && (
                  <a href={normalizeURL(socialYoutube)} target="_blank" rel="noopener noreferrer">
                    <Icon iconSize={24} iconName="youtube" />
                    <span className="label">Youtube</span>
                  </a>
                )}
                {socialTiktok && (
                  <a href={normalizeURL(socialTiktok)} target="_blank" rel="noopener noreferrer">
                    <Icon iconSize={22} iconName="tiktok" />
                    <span className="label">Tiktok</span>
                  </a>
                )}
                {customLinks.map(link => (
                  <a key={link.url} href={normalizeURL(link.url)} target="_blank" rel="noopener noreferrer">
                    {link.image ? (
                      <img src={link.image} alt={link.label} style={{ width: 24, height: 24, objectFit: 'contain' }} />
                    ) : (
                      <Icon iconSize={24} iconName="website" />
                    )}
                    <span className="label">{link.label}</span>
                  </a>
                ))}
              </StyledSocialIcons>
            </StyledColumn>
            {isEvent && (
              <StyledColumn>
                <StyledColumnLabel>{eventName || 'Event Information'}</StyledColumnLabel>
                <StyledColumnLinks>
                  {website && (
                    <a href={`https://events.mygameday.app/event/${subdomain}`} target="_blank" rel="noreferrer">
                      <Icon iconSize={24} iconName="website" />
                      Event Website
                    </a>
                  )}
                  {phone && (
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(addressString)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon iconSize={24} iconName="location" />
                      {venueName}
                      <br />
                      {addressString}
                    </a>
                  )}
                </StyledColumnLinks>
              </StyledColumn>
            )}
            {sortedPaymentGateways.length > 0 ? (
              <StyledColumn>
                <StyledColumnLabel>Payment Providers</StyledColumnLabel>
                <StyledPartners>
                  {sortedPaymentGateways.map(partner => (
                    <StyledPartner
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: TODO
                      dangerouslySetInnerHTML={{
                        __html: partner.logoCode,
                      }}
                      key={partner.sfid}
                    />
                  ))}
                </StyledPartners>
              </StyledColumn>
            ) : null}
          </StyledColumnWrapper>
        </StyledFooterInner>
      </StyledFooterMain>
      <StyledFooterBottom>
        <StyledGamedayLogo src={gameDayLogoSvg} />
        <StyledFooterText>
          Copyright GameDay. All rights reserved. <strong>Powered by GameDay</strong>
          <a href="https://mygameday.app/privacy-policy/" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
        </StyledFooterText>
      </StyledFooterBottom>
    </StyledFooter>
  )
}

export default RegistrationFooter
